import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { pathOr } from 'ramda';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

import { DropDown } from 'components/FormSelect/FormSelect.helpers';

type Marketplace = {
  name: string;
  fullName: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
};

type QueryResponse = {
  data: {
    getMarketplacesCache: [Marketplace];
  };
  loading: boolean;
};

const GET_MARKETPLACES = gql`
  query GetMarketplaces {
    getMarketplacesCache {
      name
      fullName
    }
  }
`;

const DefaultOptions = {
  options: [{ key: 'select', value: '', text: 'Select' }],
  label: {
    children: '',
  },
  placeholder: '',
};

const marketplaceDropDownOptions = (marketplaces: Marketplace[]) =>
  marketplaces.map(
    ({ name, fullName }: { name: string; fullName: string }) => ({
      key: name,
      text: fullName || name,
      value: name.toUpperCase(),
    }),
  );

const useGetMarketplaces = (query: DocumentNode = GET_MARKETPLACES) => {
  const [Marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [marketplacesMap, setMarketplacesMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [MarketplaceOptions, setMarketplaceOptions] = useState<DropDown>(
    DefaultOptions,
  );
  const { data: getMarketplacesCache, loading } = useQuery<QueryResponse>(
    query,
  );

  useEffect(() => {
    if (loading) return;
    const marketplaces = pathOr(
      [],
      ['getMarketplacesCache'],
      getMarketplacesCache,
    );
    const options = marketplaceDropDownOptions(marketplaces);
    const MarketplaceOptions = {
      options: [
        { key: 'select', value: null, text: 'Select' },
        {
          key: 'all',
          value: 'ALL',
          text: 'ALL',
        },
        {
          key: 'pbOnly',
          value: 'PB_ONLY',
          text: 'PB_ONLY',
        },
        ...options,
      ],
      label: {
        children: 'Marketplace',
      },
      placeholder: 'Select Marketplace',
    };

    setMarketplaces(marketplaces);
    setMarketplaceOptions(MarketplaceOptions);
  }, [getMarketplacesCache, loading]);

  useEffect(() => {
    if (Marketplaces.length !== 0) {
      const marketplacesMap: Map<string, string> = new Map();
      Marketplaces.forEach((marketplace) => {
        marketplacesMap.set(marketplace.name, marketplace.fullName);
      });
      setMarketplacesMap(marketplacesMap);
    }
  }, [Marketplaces]);

  const buildMarketplaces = (marketplaces: string[]) => {
    if (marketplaces.includes('ALL')) {
      return Marketplaces.map(({ name }: { name: string }) =>
        name.toUpperCase(),
      );
    }
    if (marketplaces.includes('PB_ONLY')) {
      return Marketplaces.map(({ name }: { name: string }) =>
        name.toUpperCase(),
      ).filter((marketplace) => marketplace.toUpperCase() !== 'DOSH');
    }
    return marketplaces;
  };

  return {
    loading,
    Marketplaces,
    MarketplaceOptions,
    buildMarketplaces,
    marketplacesMap,
  };
};

export { QueryResponse, Marketplace, useGetMarketplaces };
